import React, { useEffect, useState } from "react";
import { CalendarDays, Flag, Trophy } from "lucide-react";

// ============================================================
// LandingHero — three rotating "real app" screens shown inside
// a tilted phone bezel on the landing page. Auto-cycles every
// 4s. The screens are code-rendered (not stock images) so they
// stay in sync with the actual brand tokens.
//
// 1. Eclectic scorecard — features a HOLE-IN-ONE, EXCELLENT 2,
//    birdie, par, bogeys, on different dates.
// 2. Player dashboard — Welcome back + your competitions card.
// 3. Competition summary — Eclectic Net + Position stat cards.
// ============================================================

const SCREEN_LABELS = ["Your eclectic scorecard", "Player dashboard", "Competition summary"];

export default function LandingHero() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % 3), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative mx-auto" style={{ maxWidth: 340 }}>
      <div className="relative rounded-[34px] bg-ink p-2.5 shadow-2xl">
        <div className="relative rounded-[26px] bg-app overflow-hidden" style={{ aspectRatio: "9/19.2" }}>
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-ink rounded-full z-20" />
          {[0, 1, 2].map((i) => (
            <div key={i} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === idx ? 1 : 0 }}>
              {i === 0 && <ScorecardScreen />}
              {i === 1 && <DashboardScreen />}
              {i === 2 && <CompSummaryScreen />}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-4">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-accent" : "w-1.5 bg-line"}`} />
        ))}
      </div>
      <p className="text-[12px] text-ink-soft text-center mt-2" data-testid="hero-screen-label">{SCREEN_LABELS[idx]}</p>
    </div>
  );
}

// ---------- Screen 1: Eclectic Scorecard (with HIO + Excellent 2) ----------
// Heathmoor Park (fictional UK course) Front 9 par 35 layout. Holes 3 & 5 are
// both par 3s so the EXCELLENT 2 and HOLE-IN-ONE landings are realistic.
function ScorecardScreen() {
  const holes = [
    { h: 1, s: 5, p: 4, d: "07 Apr" },
    { h: 2, s: 7, p: 4, d: "07 Apr" },
    { h: 3, s: 2, p: 3, d: "07 Apr", excellent: true },
    { h: 4, s: 5, p: 4, d: "08 Apr" },
    { h: 5, s: 1, p: 3, d: "08 Apr", hio: true },
    { h: 6, s: 4, p: 5, d: "07 Apr" },
    { h: 7, s: 4, p: 4, d: "07 Apr" },
    { h: 8, s: 5, p: 4, d: "08 Apr" },
    { h: 9, s: 5, p: 4, d: "07 Apr" },
  ];
  const total = holes.reduce((a, h) => a + h.s, 0);
  const par = holes.reduce((a, h) => a + h.p, 0);
  return (
    <div className="absolute inset-0 pt-9 pb-2 px-2.5 bg-app overflow-hidden">
      <div className="px-1 pb-2">
        <p className="text-[8.5px] uppercase tracking-[0.14em] text-accent font-semibold">Best eclectic score</p>
        <p className="text-[13px] font-semibold text-ink mt-0.5">Heathmoor Park · Front 9</p>
        <p className="text-[10px] text-ink-muted mt-0.5">par {par} · <span className="font-mono">+{total - par} to par</span></p>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {holes.map((h) => {
          const diff = h.s - h.p;
          const special = h.hio || h.excellent;
          const cls = special
            ? "ring-1 ring-warm bg-warm-tint/40 border border-warm"
            : "border border-line bg-white";
          const num = special ? "text-warm" : diff < 0 ? "text-accent" : diff === 0 ? "text-ink" : "text-ink-muted";
          return (
            <div key={h.h} className={`rounded-md p-1.5 ${cls}`}>
              <div className="flex items-center justify-between">
                <span className="text-[7.5px] font-semibold uppercase tracking-[0.06em] text-ink">Hole {h.h}</span>
                <span className="text-[6.5px] font-mono px-1 rounded bg-app text-ink-muted border border-line">par {h.p}</span>
              </div>
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className={`font-mono tabular-nums text-[20px] font-semibold leading-none ${num}`}>{h.s}</span>
                {!special && (
                  <span className={`text-[7.5px] font-mono font-semibold ${diff < 0 ? "text-accent" : diff === 0 ? "text-ink-muted" : "text-ink-muted"}`}>
                    {diff === 0 ? "E" : diff > 0 ? `+${diff}` : `${diff}`}
                  </span>
                )}
              </div>
              <div className="text-[7px] text-ink-muted mt-0.5 font-medium">{h.d} 2026</div>
              {h.hio && <div className="text-[6.5px] text-warm font-semibold mt-0.5 uppercase tracking-wider">Hole-in-one</div>}
              {h.excellent && <div className="text-[6.5px] text-warm font-semibold mt-0.5 uppercase tracking-wider">Excellent 2</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Screen 2: Player Dashboard ----------
function DashboardScreen() {
  return (
    <div className="absolute inset-0 pt-9 pb-3 px-3 bg-app overflow-hidden">
      <p className="text-[8.5px] uppercase tracking-[0.14em] text-ink-muted font-semibold">Welcome back</p>
      <p className="text-[18px] font-semibold tracking-tight text-ink leading-tight mt-0.5">Lara Tortie</p>
      <p className="text-[9px] text-ink-muted mt-1">2 active competitions · keep building your perfect round.</p>

      {/* New comp banner */}
      <div className="mt-3 rounded-lg bg-accent-tint border border-accent/30 p-2 flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-accent text-white grid place-items-center text-[10px] font-bold">+</div>
        <p className="text-[8.5px] font-semibold text-accent-deep flex-1 truncate">1 new comp open to join</p>
        <span className="text-[7.5px] px-1.5 py-0.5 rounded bg-ink text-white font-semibold">View</span>
      </div>

      {/* Your competitions */}
      <div className="mt-3 bg-white rounded-lg border border-line p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[9px] font-semibold text-ink">Your competitions</p>
          <span className="text-[7.5px] px-1.5 rounded-full bg-accent-tint text-accent-deep font-semibold">2</span>
        </div>
        {[
          { n: "Spring Eclectic 2026", d: "1 Mar → 31 May", rounds: "3/10", live: true },
          { n: "Winter Knockout", d: "1 Nov → 30 Apr", rounds: "5/8", live: false },
        ].map((c) => (
          <div key={c.n} className="flex items-center gap-2 py-1.5 border-t border-line first:border-0">
            <div className="w-6 h-6 rounded-md bg-accent-tint grid place-items-center">
              <Flag className="w-3 h-3 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-semibold text-ink truncate">{c.n}</p>
              <p className="text-[7.5px] text-ink-muted truncate">{c.d}</p>
            </div>
            <span className="text-[7.5px] font-mono tabular-nums text-ink-muted">{c.rounds}</span>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        {["Guide", "Settings", "Sign out"].map((t) => (
          <div key={t} className="bg-white rounded-md border border-line p-1.5 text-center">
            <p className="text-[8px] font-semibold text-ink">{t}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Screen 3: Competition Summary ----------
function CompSummaryScreen() {
  return (
    <div className="absolute inset-0 pt-9 pb-3 px-3 bg-app overflow-hidden">
      <p className="text-[8.5px] uppercase tracking-[0.14em] text-ink-muted font-semibold">Spring Eclectic</p>
      <p className="text-[16px] font-semibold tracking-tight text-ink mt-0.5">Your competition</p>
      <p className="text-[9px] text-ink-muted mt-0.5 flex items-center gap-1">
        <CalendarDays className="w-2.5 h-2.5" /> 4 of 10 rounds played · ends 31 May
      </p>

      {/* 2x2 stat grid */}
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <div className="bg-white rounded-lg border border-line p-2">
          <p className="text-[7.5px] uppercase tracking-[0.06em] text-ink-muted font-semibold">Eclectic Net</p>
          <p className="text-[22px] font-semibold text-accent tabular-nums leading-none mt-1">33</p>
          <p className="text-[7.5px] text-ink-muted mt-1">Gross 45</p>
        </div>
        <div className="bg-white rounded-lg border border-line p-2">
          <p className="text-[7.5px] uppercase tracking-[0.06em] text-ink-muted font-semibold">Position</p>
          <p className="text-[22px] font-semibold text-ink tabular-nums leading-none mt-1">
            4<span className="text-ink-muted text-[14px]">/18</span>
          </p>
          <p className="text-[7.5px] text-ink-muted mt-1">1.5 strokes from 1st</p>
        </div>
        <div className="bg-white rounded-lg border border-line p-2">
          <p className="text-[7.5px] uppercase tracking-[0.06em] text-ink-muted font-semibold">Rounds</p>
          <p className="text-[22px] font-semibold text-ink tabular-nums leading-none mt-1">
            4<span className="text-ink-muted text-[14px]">/10</span>
          </p>
          <p className="text-[7.5px] text-ink-muted mt-1">6 left in this comp</p>
        </div>
        <div className="bg-white rounded-lg border border-line p-2">
          <p className="text-[7.5px] uppercase tracking-[0.06em] text-ink-muted font-semibold">Best hole</p>
          <p className="text-[22px] font-semibold text-warm tabular-nums leading-none mt-1">
            2<span className="text-ink-muted text-[12px] font-medium ml-1">on 3</span>
          </p>
          <p className="text-[7.5px] text-warm font-semibold mt-1 uppercase tracking-wide">Excellent 2</p>
        </div>
      </div>

      {/* Mini leaderboard */}
      <div className="mt-3 bg-white rounded-lg border border-line p-2">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Trophy className="w-3 h-3 text-warm" />
          <p className="text-[9px] font-semibold text-ink">Leaderboard</p>
        </div>
        {[
          { r: 1, n: "M. Vance", s: 31, gold: true },
          { r: 2, n: "J. Chen", s: 32 },
          { r: 4, n: "You", s: 33, you: true },
        ].map((p) => (
          <div key={p.r} className={`flex items-center gap-2 px-1 py-1 rounded ${p.you ? "bg-accent-tint-2" : ""}`}>
            <span className={`text-[8px] font-mono w-3 ${p.gold ? "text-warm font-bold" : "text-ink-muted"}`}>{p.r}</span>
            <span className="flex-1 text-[9px] font-medium text-ink truncate">{p.n}</span>
            <span className="text-[10px] font-mono tabular-nums font-semibold text-ink">{p.s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
